// src/utils/wsSingleton.ts
type Handler = (msg: any) => void;

class SimpleEmitter {
  private map = new Map<string, Set<Handler>>();
  
  on(ev: string, h: Handler) {
    if (!this.map.has(ev)) this.map.set(ev, new Set());
    this.map.get(ev)!.add(h);
  }
  
  off(ev: string, h: Handler) {
    this.map.get(ev)?.delete(h);
  }
  
  emit(ev: string, payload: any) {
    this.map.get(ev)?.forEach(h => {
      try { h(payload); } catch (e) { console.error('ws emitter handler error', e); }
    });
  }
}

class WsSingleton {
  private ws: WebSocket | null = null;
  private url = '';
  private emitter = new SimpleEmitter();
  private reconnectTimer: number | null = null;
  private closedByMe = false;
  private isConnecting = false;
  private backoff = 1000;

  // 通知相关状态
  private notificationQueue: any[] = [];
  private isNotificationReady = false;

  init(url: string) {
    if (this.ws && this.url === url) return;
    this.url = url;
    this.closedByMe = false;
    this.connect();
  }

  private connect() {
    if (this.isConnecting) return;
    this.isConnecting = true;
    
    try {
      console.log('[WsSingleton] connecting ->', this.url);
      this.ws = new WebSocket(this.url);
    } catch (e) {
      console.error('[WsSingleton] ctor failed', e);
      this.scheduleReconnect();
      this.isConnecting = false;
      return;
    }

    this.ws.onopen = () => {
      console.log('[WsSingleton] connected');
      this.backoff = 1000;
      this.isConnecting = false;
      this.isNotificationReady = true;
      
      // 发送积压的通知
      this.flushNotificationQueue();
      
      this.emitter.emit('__internal_open', null);
    };

    this.ws.onmessage = (evt) => {
      let msg: any = null;
      try { 
        msg = typeof evt.data === 'string' ? JSON.parse(evt.data) : evt.data; 
      } catch (e) {
        msg = evt.data;
        console.warn('[WsSingleton] message parse error', e);
      }
      
      if (msg && typeof msg === 'object' && msg.type) {
        this.emitter.emit(msg.type, msg);
        
        // 特殊处理通知相关消息
        if (msg.type === 'notification') {
          this.handleNotificationMessage(msg);
        } else if (msg.type === 'notification_read') {
          this.handleNotificationReadMessage(msg);
        }
      } else {
        this.emitter.emit('__raw', msg);
      }
    };

    this.ws.onclose = (ev) => {
      console.warn('[WsSingleton] onclose', ev.code, ev.reason, ev.wasClean);
      this.ws = null;
      this.isNotificationReady = false;
      if (!this.closedByMe) this.scheduleReconnect();
      this.emitter.emit('__internal_close', ev);
    };

    this.ws.onerror = (err) => {
      console.error('[WsSingleton] onerror', err);
      this.emitter.emit('__internal_error', err);
    };
  }

  // 处理通知消息
  private handleNotificationMessage(msg: any) {
    console.log('[WsSingleton] 收到通知消息:', msg);
    this.emitter.emit('new_notification', msg.data || msg);
  }

  // 处理通知已读消息
  private handleNotificationReadMessage(msg: any) {
    console.log('[WsSingleton] 收到通知已读消息:', msg);
    this.emitter.emit('notification_read', msg.data || msg);
  }

  // 发送通知相关消息
  sendNotification(action: string, data: any) {
    const message = {
      type: 'notification',
      action,
      timestamp: new Date().toISOString(),
      data
    };
    
    if (this.isNotificationReady) {
      return this.send(message);
    } else {
      // 如果连接未就绪，先缓存消息
      this.notificationQueue.push(message);
      return false;
    }
  }

  // 标记通知为已读
  markNotificationAsRead(notificationId: string, userId: string) {
    return this.sendNotification('mark_as_read', {
      notificationId,
      userId
    });
  }

  // 订阅通知 - 修复方法名：统一使用 subscribeToNotifications
  subscribeToNotifications(userId: string) {
    return this.sendNotification('subscribe', { userId });
  }

  // 刷新通知队列
  private flushNotificationQueue() {
    while (this.notificationQueue.length > 0) {
      const message = this.notificationQueue.shift();
      this.send(message);
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    const delay = Math.min(this.backoff, 30_000);
    console.log('[WsSingleton] scheduleReconnect in', delay);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.backoff = Math.min(30000, this.backoff * 1.5);
      this.connect();
    }, delay);
  }

  send(obj: any) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    try { 
      this.ws.send(JSON.stringify(obj)); 
      return true; 
    } catch (e) { 
      console.error('[WsSingleton] send error', e);
      return false; 
    }
  }

  on(event: string, handler: Handler) { this.emitter.on(event, handler); }
  off(event: string, handler: Handler) { this.emitter.off(event, handler); }

  close() {
    this.closedByMe = true;
    this.isNotificationReady = false;
    if (this.reconnectTimer) { 
      window.clearTimeout(this.reconnectTimer); 
      this.reconnectTimer = null; 
    }
    try { this.ws?.close(); } catch (e) {
      console.error('[WsSingleton] close error', e);
    }
    this.ws = null;
  }

  // 获取连接状态
  get readyState() {
    return this.ws?.readyState;
  }

  get isReady() {
    return this.isNotificationReady;
  }
}

const singleton = new WsSingleton();
export default singleton;